import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChartLine, ArrowRight, ArrowLeft, Check, Buildings, Scales, GearSix, Sliders } from '@phosphor-icons/react';

const STEPS = [
  { key: 'identity', label: 'Company Identity', icon: Buildings },
  { key: 'accounting', label: 'Accounting Policy', icon: Scales },
  { key: 'operations', label: 'Operations', icon: GearSix },
  { key: 'preferences', label: 'Preferences', icon: Sliders },
  { key: 'complete', label: 'Complete', icon: Check }
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', company_name: '', industry: '', entity_structure: '', business_model: '',
    departments: '', revenue_streams: '', locations: '',
    accounting_basis: 'accrual', capitalization_threshold: '', prepaid_expense_policy: '',
    revenue_recognition_notes: '', loan_handling_rules: '', owner_compensation_logic: '',
    seasonality: '', top_vendors: '', common_payment_rails: '', monthly_close_process: '',
    expected_expense_cycles: '',
    automation_level: 'moderate', review_threshold: '70', materiality_threshold: ''
  });

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const splitList = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];

  const handleFinish = async () => {
    setSaving(true);
    try {
      await createClient({
        name: form.name || form.company_name || 'New Client',
        company_identity: {
          company_name: form.company_name, industry: form.industry, entity_structure: form.entity_structure,
          business_model: form.business_model, departments: splitList(form.departments),
          revenue_streams: splitList(form.revenue_streams), locations: splitList(form.locations)
        },
        accounting_policy: {
          accounting_basis: form.accounting_basis,
          capitalization_threshold: form.capitalization_threshold ? parseFloat(form.capitalization_threshold) : null,
          prepaid_expense_policy: form.prepaid_expense_policy, revenue_recognition_notes: form.revenue_recognition_notes,
          loan_handling_rules: form.loan_handling_rules, owner_compensation_logic: form.owner_compensation_logic
        },
        operational_context: {
          seasonality: form.seasonality, top_vendors: splitList(form.top_vendors),
          common_payment_rails: splitList(form.common_payment_rails),
          monthly_close_process: form.monthly_close_process, expected_expense_cycles: form.expected_expense_cycles
        },
        user_preferences: {
          automation_level: form.automation_level,
          review_threshold: parseInt(form.review_threshold) || 70,
          materiality_threshold: form.materiality_threshold ? parseFloat(form.materiality_threshold) : null
        }
      });
      toast.success('Client profile created!');
      setStep(4); // complete step
    } catch (error) {
      toast.error('Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  const progress = Math.round((step / (STEPS.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="h-16 border-b border-zinc-200 flex items-center px-8 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-zinc-950 flex items-center justify-center">
            <ChartLine size={18} weight="bold" className="text-white" />
          </div>
          <span className="font-semibold tracking-tight">AI Snugglemind</span>
          <span className="text-zinc-400 mx-2">/</span>
          <span className="text-zinc-500 text-sm">Client Onboarding</span>
        </div>
        <Button variant="outline" onClick={() => navigate('/clients')} className="rounded-sm text-sm">
          Skip for now
        </Button>
      </header>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-8 pt-8">
        <div className="flex items-center gap-2 mb-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex items-center gap-2 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-400'
                }`}>
                  {i < step ? <Check size={14} /> : <Icon size={14} />}
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-emerald-500' : 'bg-zinc-200'}`}></div>}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-zinc-500 mb-8">{STEPS[step]?.label} — Step {step + 1} of {STEPS.length}</p>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-8 pb-24">
        {step === 0 && (
          <div className="space-y-6" data-testid="wizard-step-identity">
            <div><h2 className="text-2xl font-bold tracking-tight mb-1">Company Identity</h2><p className="text-zinc-500">Tell us about the business so AI can understand the context.</p></div>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Profile Name</Label><Input value={form.name} onChange={(e) => updateForm('name', e.target.value)} placeholder="e.g., Acme Corp" className="rounded-sm" data-testid="wiz-name" /></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Legal Name</Label><Input value={form.company_name} onChange={(e) => updateForm('company_name', e.target.value)} placeholder="Acme Corporation Inc." className="rounded-sm" data-testid="wiz-company" /></div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Industry</Label>
                <Select value={form.industry} onValueChange={(v) => updateForm('industry', v)}><SelectTrigger className="rounded-sm" data-testid="wiz-industry"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="technology">Technology</SelectItem><SelectItem value="law_firm">Law Firm</SelectItem><SelectItem value="retail">Retail</SelectItem><SelectItem value="manufacturing">Manufacturing</SelectItem><SelectItem value="professional_services">Professional Services</SelectItem><SelectItem value="healthcare">Healthcare</SelectItem><SelectItem value="construction">Construction</SelectItem><SelectItem value="real_estate">Real Estate</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Entity Structure</Label>
                <Select value={form.entity_structure} onValueChange={(v) => updateForm('entity_structure', v)}><SelectTrigger className="rounded-sm" data-testid="wiz-entity"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem><SelectItem value="llc">LLC</SelectItem><SelectItem value="s_corp">S Corp</SelectItem><SelectItem value="c_corp">C Corp</SelectItem><SelectItem value="partnership">Partnership</SelectItem><SelectItem value="nonprofit">Non-profit</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Business Model</Label><Textarea value={form.business_model} onChange={(e) => updateForm('business_model', e.target.value)} placeholder="e.g., SaaS with annual contracts, B2B consulting" className="rounded-sm resize-none" rows={2} /></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Departments (comma-separated)</Label><Input value={form.departments} onChange={(e) => updateForm('departments', e.target.value)} placeholder="Sales, Engineering, Marketing" className="rounded-sm" /></div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6" data-testid="wizard-step-accounting">
            <div><h2 className="text-2xl font-bold tracking-tight mb-1">Accounting Policy</h2><p className="text-zinc-500">These hard rules override AI pattern recognition.</p></div>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Accounting Basis</Label>
                <Select value={form.accounting_basis} onValueChange={(v) => updateForm('accounting_basis', v)}><SelectTrigger className="rounded-sm" data-testid="wiz-basis"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="accrual">Accrual</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Capitalization Threshold ($)</Label><Input type="number" value={form.capitalization_threshold} onChange={(e) => updateForm('capitalization_threshold', e.target.value)} placeholder="e.g., 2500" className="rounded-sm font-mono" data-testid="wiz-cap" /></div>
            </div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Loan Handling Rules</Label><Textarea value={form.loan_handling_rules} onChange={(e) => updateForm('loan_handling_rules', e.target.value)} placeholder="e.g., Principal to Notes Payable, interest to Interest Expense" className="rounded-sm resize-none" rows={2} /></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Owner Compensation Logic</Label><Textarea value={form.owner_compensation_logic} onChange={(e) => updateForm('owner_compensation_logic', e.target.value)} placeholder="e.g., Owner takes reasonable salary via payroll, draws as distributions" className="rounded-sm resize-none" rows={2} /></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Revenue Recognition Notes</Label><Textarea value={form.revenue_recognition_notes} onChange={(e) => updateForm('revenue_recognition_notes', e.target.value)} placeholder="e.g., SaaS revenue recognized ratably over subscription" className="rounded-sm resize-none" rows={2} /></div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6" data-testid="wizard-step-operations">
            <div><h2 className="text-2xl font-bold tracking-tight mb-1">Operational Context</h2><p className="text-zinc-500">Business operations that inform transaction interpretation.</p></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Seasonality</Label><Textarea value={form.seasonality} onChange={(e) => updateForm('seasonality', e.target.value)} placeholder="e.g., Q4 is peak revenue, Jan-Feb are slow" className="rounded-sm resize-none" rows={2} /></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Top Vendors (comma-separated)</Label><Input value={form.top_vendors} onChange={(e) => updateForm('top_vendors', e.target.value)} placeholder="AWS, Google, ADP, Slack" className="rounded-sm" /></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Payment Rails (comma-separated)</Label><Input value={form.common_payment_rails} onChange={(e) => updateForm('common_payment_rails', e.target.value)} placeholder="ACH, Wire, Credit Card" className="rounded-sm" /></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Monthly Close Process</Label><Textarea value={form.monthly_close_process} onChange={(e) => updateForm('monthly_close_process', e.target.value)} placeholder="e.g., Close by 10th, reconcile bank by 5th" className="rounded-sm resize-none" rows={2} /></div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6" data-testid="wizard-step-preferences">
            <div><h2 className="text-2xl font-bold tracking-tight mb-1">Automation Preferences</h2><p className="text-zinc-500">How aggressively should the AI act on decisions?</p></div>
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Automation Level</Label>
                <Select value={form.automation_level} onValueChange={(v) => updateForm('automation_level', v)}><SelectTrigger className="rounded-sm" data-testid="wiz-automation"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="conservative">Conservative</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="aggressive">Aggressive</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Review Threshold (%)</Label><Input type="number" value={form.review_threshold} onChange={(e) => updateForm('review_threshold', e.target.value)} className="rounded-sm font-mono" /></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-zinc-500">Materiality ($)</Label><Input type="number" value={form.materiality_threshold} onChange={(e) => updateForm('materiality_threshold', e.target.value)} placeholder="e.g., 5000" className="rounded-sm font-mono" /></div>
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm text-zinc-600">
              <p><strong>Conservative:</strong> Reviews most transactions. Best for new clients.</p>
              <p><strong>Moderate:</strong> Balanced auto-posting and review. Recommended default.</p>
              <p><strong>Aggressive:</strong> Auto-posts more. Only for well-established patterns.</p>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-12" data-testid="wizard-step-complete">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={32} weight="bold" className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight mb-2">Profile Created!</h2>
            <p className="text-zinc-500 mb-8 max-w-md mx-auto">
              The business context profile is ready. The AI will reference it on every transaction decision.
              You can always edit the profile later to add more detail.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => navigate('/clients')} className="rounded-sm">View Profiles</Button>
              <Button onClick={() => navigate('/transactions')} className="bg-zinc-950 text-white rounded-sm">Start Adding Transactions</Button>
            </div>
          </div>
        )}

        {/* Navigation */}
        {step < 4 && (
          <div className="flex justify-between mt-12 pt-6 border-t border-zinc-200">
            <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="rounded-sm" data-testid="wiz-back">
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
            {step < 3 ? (
              <Button onClick={() => setStep(s => s + 1)} className="bg-zinc-950 text-white rounded-sm" data-testid="wiz-next">
                Next <ArrowRight size={16} className="ml-2" />
              </Button>
            ) : (
              <Button onClick={handleFinish} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="wiz-finish">
                {saving ? 'Creating...' : 'Create Profile'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
